import { ERoles } from '@constants';
import { IPracticeQuizFilters } from '@sdk/contracts';
import { useQuery } from '@tanstack/react-query';
import { create as createStore } from 'zustand';

import { useProfile } from '../profile';

import { sdk } from '@/services';

const INITIAL_PAGE = 1;
const INITIAL_LIMIT = 50;

const QuizService = sdk.getService('QuizService');

interface IStore {
  page: number;
  limit: number;
  search: string;
  filter?: IPracticeQuizFilters;
  setFilter: (filter?: IPracticeQuizFilters) => void;
  setSearch: (search: string) => void;
  setPageAndLimit: (page: number, limit: number) => void;
}

const store = createStore<IStore>(set => ({
  page: INITIAL_PAGE,
  limit: INITIAL_LIMIT,
  search: '',
  setSearch: (search: string) => set(state => ({ ...state, search, page: INITIAL_PAGE })),
  setFilter: (filter?: IPracticeQuizFilters) =>
    set(state => ({ ...state, page: INITIAL_PAGE, filter })),
  setPageAndLimit: (page: number, limit: number) => set(state => ({ ...state, page, limit }))
}));

export const usePracticeQuizzes = (fetchAll = false) => {
  const { profile } = useProfile();
  const { page, limit, filter, setFilter, setPageAndLimit, search, setSearch } = store();
  const isEnabled = profile?.role === ERoles.STUDENT ? true : !!filter?.tag;

  const queryKey = [
    'quizzes',
    'practice',
    `page-${page}`,
    `limit-${limit}`,
    search,
    `bypass-${fetchAll}`,
    filter
  ];

  const { isLoading, data } = useQuery({
    queryKey,
    queryFn: () => QuizService.getPracticeQuizzes(search, { page, limit }, fetchAll, filter),
    enabled: isEnabled
  });

  const pagination = data && {
    page: data.page,
    limit: data.limit,
    hasNextPage: data.hasNextPage,
    hasPrevPage: data.hasPrevPage,
    nextPage: data.nextPage,
    pagingCounter: data.pagingCounter,
    prevPage: data.prevPage,
    totalDocs: data.totalDocs,
    totalPages: data.totalPages
  };

  return {
    practiceQuizzes: data?.docs,
    isLoading,
    pagination,
    setPageAndLimit,
    setSearch,
    filter,
    setFilter
  };
};
